/**
 * RECRUTEES DESIGN SYSTEM TOKENS
 * Single source of truth for all design decisions
 * Last updated: 2025-01-16
 */

// ============================================
// TYPOGRAPHY SCALE
// ============================================
export const typography = {
  // Display - Reserved for marketing/hero sections
  display: {
    large: 'text-6xl md:text-7xl font-bold tracking-tight',
    medium: 'text-5xl md:text-6xl font-bold tracking-tight',
    small: 'text-4xl md:text-5xl font-bold tracking-tight',
  },
  
  // Headings - For page/section structure
  h1: 'text-4xl font-bold tracking-tight',
  h2: 'text-2xl font-semibold tracking-tight',
  h3: 'text-lg font-semibold',
  h4: 'text-base font-semibold',
  
  // Body text variants
  body: {
    large: 'text-lg leading-relaxed',
    default: 'text-base leading-normal',
    small: 'text-sm leading-normal',
  },
  
  // Utility text
  caption: 'text-xs leading-tight',
  overline: 'text-xs uppercase tracking-wide font-semibold',
  
  // Interactive elements
  label: 'text-sm font-medium',
  button: {
    large: 'text-base font-semibold',
    default: 'text-sm font-semibold',
    small: 'text-xs font-semibold',
  },
};

// ============================================
// SPACING SYSTEM
// ============================================
export const spacing = {
  // Layout spacing - Major sections and page structure
  section: 'gap-8 md:gap-12',        // Between major page sections
  container: 'gap-6 md:gap-8',       // Within container sections
  
  // Component spacing - Between related components
  component: 'gap-4 md:gap-6',       // Between related components
  group: 'gap-3 md:gap-4',           // Between grouped elements
  
  // Inline spacing - Within components
  inline: 'gap-2',                   // Icons + text, button groups
  tight: 'gap-1',                    // Very tight spacing (badges, chips)
  
  // Padding presets
  padding: {
    section: 'px-4 py-8 md:px-6 md:py-12',
    container: 'px-4 py-6 md:px-6 md:py-8',
    component: 'p-4 md:p-6',
    compact: 'p-3 md:p-4',
    tight: 'p-2',
  },
};

// ============================================
// CARD SYSTEM
// ============================================
export const cards = {
  // Base card styles
  base: 'bg-card rounded-lg border border-border',
  
  // Card variants with padding
  default: 'bg-card rounded-lg border border-border p-6',
  large: 'bg-card rounded-lg border border-border p-8',
  compact: 'bg-card rounded-lg border border-border p-4',
  
  // Card with shadow
  elevated: 'bg-card rounded-lg border border-border shadow-md',
  
  // Interactive cards
  interactive: 'bg-card rounded-lg border border-border p-6 hover:shadow-lg hover:border-primary/20 transition-all duration-200 cursor-pointer',
  
  // Special variants
  feature: 'bg-card rounded-lg border border-border p-8 shadow-sm hover:shadow-md transition-shadow',
  modal: 'bg-background rounded-lg border border-border shadow-xl p-6',
};

// ============================================
// BUTTON HIERARCHY
// ============================================
export const buttons = {
  // Usage guidelines
  usage: {
    primary: 'Main call-to-action (Submit, Save, Create, Sign Up)',
    secondary: 'Secondary actions (Cancel, Edit, View Details)',
    ghost: 'Tertiary actions (Navigation links, less important actions)',
    outline: 'Alternative secondary actions, filter buttons',
    link: 'Text-only links within content',
    destructive: 'Delete, Remove, Dangerous actions',
  },
  
  // Size variants
  sizes: {
    large: 'h-12 px-6 text-base',
    default: 'h-10 px-4 text-sm',
    small: 'h-8 px-3 text-xs',
    icon: 'h-10 w-10',
  },
  
  // Compound classes for common patterns
  primaryAction: 'bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 text-sm font-semibold rounded-md transition-colors',
  secondaryAction: 'bg-secondary text-secondary-foreground hover:bg-secondary/80 h-10 px-4 text-sm font-semibold rounded-md transition-colors',
};

// ============================================
// COLOR TOKEN MAPPING
// ============================================
export const colors = {
  // STANDARDIZED NAMING CONVENTION
  // Use these tokens consistently throughout the app
  
  // Text colors (STANDARD - Use these)
  text: {
    primary: 'text-foreground',           // Main text - WCAG AAA compliant
    secondary: 'text-muted-foreground',   // Less emphasis - WCAG AA compliant  
    muted: 'text-muted-foreground',       // Deemphasized text - WCAG AA compliant (consolidated from text-muted-foreground/70)
    inverse: 'text-primary-foreground',   // Text on dark backgrounds - WCAG AAA compliant
  },
  
  // Background colors
  background: {
    primary: 'bg-background',
    secondary: 'bg-muted',
    card: 'bg-card',
    elevated: 'bg-card',
    input: 'bg-input',
  },
  
  // Interactive colors
  interactive: {
    primary: 'text-primary hover:text-primary/90',
    secondary: 'text-secondary hover:text-secondary/90',
    accent: 'text-accent hover:text-accent/90',
  },
  
  // Status colors (WCAG AA compliant)
  status: {
    success: 'text-green-600 dark:text-green-400',
    warning: 'text-yellow-600 dark:text-yellow-400',
    error: 'text-red-600 dark:text-red-400',
    info: 'text-blue-600 dark:text-blue-400',
  },
  
  // Border colors
  border: {
    default: 'border-border',
    subtle: 'border-border/50',
    strong: 'border-border/80',
    interactive: 'border-primary',
  },
  
  // DEPRECATED TOKENS - DO NOT USE
  // These will be removed in future versions
  deprecated: {
    'text-text': 'Use colors.text.primary instead',
    'text-muted': 'Use colors.text.secondary or colors.text.muted instead',
    'bg-surface': 'Use colors.background.card instead',
    'bg-bg': 'Use colors.background.primary instead',
    'text-muted-foreground/70': 'Use colors.text.muted instead',
  }
};

// ============================================
// BORDER RADIUS SCALE
// ============================================
export const radius = {
  // Usage guidelines
  usage: {
    sm: 'Small elements (badges, chips, inline indicators)',
    md: 'Form inputs, small buttons, standard UI elements',
    lg: 'Cards, modals, large buttons, panels',
    xl: 'Hero sections, feature cards, prominent elements',
    full: 'Avatars, icon buttons, pills, circular elements',
  },
  
  // Actual values
  sm: 'rounded-sm',      // 0.125rem (2px)
  md: 'rounded-md',      // 0.375rem (6px)
  lg: 'rounded-lg',      // 0.5rem (8px)
  xl: 'rounded-xl',      // 0.75rem (12px)
  '2xl': 'rounded-2xl',  // 1rem (16px)
  full: 'rounded-full',  // 9999px
  
  // Compound classes for common patterns
  card: 'rounded-lg',
  button: 'rounded-md',
  input: 'rounded-md',
  modal: 'rounded-lg',
  badge: 'rounded-full',
  avatar: 'rounded-full',
};

// ============================================
// SHADOW LEVELS
// ============================================
export const shadows = {
  // Usage guidelines
  usage: {
    sm: 'Subtle elevation (form inputs, small cards)',
    md: 'Standard elevation (cards, buttons, dropdowns)',
    lg: 'High elevation (modals, popovers, floating elements)',
    xl: 'Maximum elevation (dialog overlays, critical alerts)',
  },
  
  // Actual values
  sm: 'shadow-sm',
  md: 'shadow-md',
  lg: 'shadow-lg',
  xl: 'shadow-xl',
  '2xl': 'shadow-2xl',
  
  // Compound classes for common patterns
  card: 'shadow-sm',
  elevated: 'shadow-md',
  modal: 'shadow-xl',
  dropdown: 'shadow-lg',
  
  // Interactive shadows
  hover: {
    card: 'hover:shadow-md transition-shadow',
    elevated: 'hover:shadow-lg transition-shadow',
  },
};

// ============================================
// ICON SIZES
// ============================================
export const icons = {
  // Usage guidelines
  usage: {
    xs: 'Inline icons within text, status indicators',
    sm: 'Default icons in buttons, form elements',
    md: 'Navigation icons, action buttons',
    lg: 'Feature highlights, section headers',
    xl: 'Hero sections, empty states',
  },
  
  // Size values
  xs: 'h-3 w-3',
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
  xl: 'h-8 w-8',
  '2xl': 'h-12 w-12',
};

// ============================================
// ANIMATION & TRANSITIONS (FRAMER MOTION)
// ============================================
export const motion = {
  // Duration presets (milliseconds for Framer Motion)
  duration: {
    fast: 0.15,      // 150ms - Quick feedback (hover states, button presses)
    normal: 0.2,     // 200ms - Standard transitions (modals, dropdowns)
    slow: 0.3,       // 300ms - Deliberate animations (page transitions, complex states)
    slower: 0.5,     // 500ms - Hero animations, emphasis
  },
  
  // Easing functions (Framer Motion format)
  easing: {
    // Standard easing - Use for most animations
    default: [0.4, 0, 0.2, 1],           // Smooth, natural motion
    
    // Specialized easing
    linear: [0, 0, 1, 1],                 // Constant speed
    easeIn: [0.4, 0, 1, 1],               // Accelerate from rest
    easeOut: [0, 0, 0.2, 1],              // Decelerate to rest
    easeInOut: [0.4, 0, 0.2, 1],          // Smooth start and end
    
    // Spring physics (for bouncy, playful animations)
    spring: {
      type: "spring",
      stiffness: 400,
      damping: 30
    },
    springGentle: {
      type: "spring",
      stiffness: 200,
      damping: 25
    }
  },
  
  // CSS Transition classes (for simple transitions)
  transition: {
    default: 'transition-all duration-200 ease-in-out',
    fast: 'transition-all duration-150 ease-in-out',
    slow: 'transition-all duration-300 ease-in-out',
    colors: 'transition-colors duration-200',
    transform: 'transition-transform duration-200',
    opacity: 'transition-opacity duration-200',
  },
  
  // Framer Motion animation variants
  variants: {
    // Page transitions (for route changes)
    page: {
      initial: { opacity: 0, y: 20 },
      animate: { 
        opacity: 1, 
        y: 0,
        transition: {
          duration: 0.3,
          ease: [0.4, 0, 0.2, 1]
        }
      },
      exit: { 
        opacity: 0, 
        y: -20,
        transition: {
          duration: 0.2,
          ease: [0.4, 0, 0.2, 1]
        }
      }
    },
    
    // Fade animations
    fadeIn: {
      initial: { opacity: 0 },
      animate: { 
        opacity: 1,
        transition: { duration: 0.2 }
      },
      exit: { 
        opacity: 0,
        transition: { duration: 0.15 }
      }
    },
    
    // Slide animations
    slideUp: {
      initial: { opacity: 0, y: 30 },
      animate: { 
        opacity: 1, 
        y: 0,
        transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] }
      }
    },
    
    slideDown: {
      initial: { opacity: 0, y: -30 },
      animate: { 
        opacity: 1, 
        y: 0,
        transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] }
      }
    },
    
    slideLeft: {
      initial: { opacity: 0, x: 30 },
      animate: { 
        opacity: 1, 
        x: 0,
        transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] }
      }
    },
    
    slideRight: {
      initial: { opacity: 0, x: -30 },
      animate: { 
        opacity: 1, 
        x: 0,
        transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] }
      }
    },
    
    // Scale animations
    scaleIn: {
      initial: { opacity: 0, scale: 0.9 },
      animate: { 
        opacity: 1, 
        scale: 1,
        transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] }
      }
    },
    
    // Stagger container (for lists and grids)
    staggerContainer: {
      animate: {
        transition: {
          staggerChildren: 0.1,
          delayChildren: 0.05
        }
      }
    },
    
    // Stagger item (use with staggerContainer)
    staggerItem: {
      initial: { opacity: 0, y: 20 },
      animate: { 
        opacity: 1, 
        y: 0,
        transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] }
      }
    },
  },
  
  // Hover animation presets
  hover: {
    // Scale hover (buttons, cards)
    scale: {
      scale: 1.05,
      transition: { duration: 0.15, ease: [0.4, 0, 0.2, 1] }
    },
    
    scaleSubtle: {
      scale: 1.02,
      transition: { duration: 0.15, ease: [0.4, 0, 0.2, 1] }
    },
    
    // Lift hover (cards)
    lift: {
      y: -4,
      transition: { duration: 0.15, ease: [0.4, 0, 0.2, 1] }
    },
    
    liftSubtle: {
      y: -2,
      transition: { duration: 0.15, ease: [0.4, 0, 0.2, 1] }
    },
    
    // Brightness hover
    brightness: {
      filter: 'brightness(1.1)',
      transition: { duration: 0.15 }
    },
  },
  
  // Tap/Press animations
  tap: {
    // Scale down on press
    scale: { scale: 0.95 },
    scaleSubtle: { scale: 0.98 },
  },
  
  // Accessibility: Reduce motion support
  // Use these when prefers-reduced-motion is active
  // Use these when prefers-reduced-motion is active
  reducedMotion: {
    // Instant transitions (no animation)
    instant: {
      duration: 0.01,
      ease: 'linear'
    },
    
    // Variants with reduced motion
    variants: {
      page: {
        initial: { opacity: 0 },
        animate: { 
          opacity: 1,
          transition: { duration: 0.01 }
        },
        exit: { 
          opacity: 0,
          transition: { duration: 0.01 }
        }
      },
      
      fadeIn: {
        initial: { opacity: 0 },
        animate: { 
          opacity: 1,
          transition: { duration: 0.01 }
        }
      },
      
      // Disable all motion
      static: {
        initial: {},
        animate: {},
        exit: {}
      }
    }
  },
  
  // Usage examples
  usage: {
    button: 'Use hover.scale and tap.scale for button interactions',
    card: 'Use hover.lift for card hover states',
    page: 'Use variants.page for route transitions',
    list: 'Use variants.staggerContainer and variants.staggerItem for lists',
    modal: 'Use variants.scaleIn for modal entrance',
  }
};

// ============================================
// FORM ELEMENTS
// ============================================
export const forms = {
  // Input fields
  input: {
    base: 'w-full px-3 py-2 bg-background border border-border rounded-md text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors shadow-sm',
    error: 'border-red-500 focus:ring-red-500',
    disabled: 'bg-muted cursor-not-allowed opacity-50',
  },
  
  // Select fields
  select: {
    base: 'w-full px-3 py-2 bg-background border border-border rounded-md text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors appearance-none cursor-pointer shadow-sm',
    error: 'border-red-500 focus:ring-red-500',
    disabled: 'bg-muted cursor-not-allowed opacity-50',
  },
  
  // Labels
  label: 'text-sm font-medium text-foreground mb-1 block',
  
  // Helper text
  helper: 'text-xs text-muted-foreground mt-1',
  error: 'text-xs text-red-600 dark:text-red-400 mt-1',
};

// ============================================
// LAYOUT CONTAINERS
// ============================================
export const layout = {
  // Page containers
  page: 'min-h-screen bg-background',
  container: 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8',
  section: 'py-8 md:py-12',
  
  // Content wrappers
  content: 'max-w-4xl',
  narrow: 'max-w-2xl',
  wide: 'max-w-6xl',
  
  // Flex patterns
  flexCenter: 'flex items-center justify-center',
  flexBetween: 'flex items-center justify-between',
  flexStart: 'flex items-center justify-start',
  flexEnd: 'flex items-center justify-end',
  flexCol: 'flex flex-col',
};

// ============================================
// RESPONSIVE BREAKPOINTS
// ============================================
export const breakpoints = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
};

// ============================================
// USAGE EXAMPLES
// ============================================
export const examples = {
  card: `
    // Standard card
    <div className={cards.default}>
      <h3 className={typography.h3}>Card Title</h3>
      <p className={colors.text.secondary}>Card content</p>
    </div>
  `,
  
  button: `
    // Primary action button
    <button className="bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 text-sm font-semibold rounded-md transition-colors">
      Submit
    </button>
  `,
  
  typography: `
    // Page heading
    <h1 className={typography.h1}>Page Title</h1>
    <p className={typography.body.default}>Body text content</p>
  `,
};

// Mobile-specific design tokens
export const mobileTokens = {
  // Touch target sizes
  touchTarget: {
    minimum: '44px',
    comfortable: '48px',
    large: '56px'
  },
  
  // Mobile spacing
  spacing: {
    mobile: {
      xs: '0.5rem',   // 8px
      sm: '0.75rem',  // 12px
      md: '1rem',     // 16px
      lg: '1.5rem',   // 24px
      xl: '2rem'      // 32px
    },
    tablet: {
      xs: '0.75rem',  // 12px
      sm: '1rem',     // 16px
      md: '1.5rem',   // 24px
      lg: '2rem',     // 32px
      xl: '3rem'      // 48px
    }
  },
  
  // Mobile typography
  typography: {
    mobile: {
      h1: { fontSize: '1.75rem', lineHeight: '2rem', fontWeight: '700' },
      h2: { fontSize: '1.5rem', lineHeight: '1.75rem', fontWeight: '700' },
      h3: { fontSize: '1.25rem', lineHeight: '1.5rem', fontWeight: '600' },
      body: { fontSize: '1rem', lineHeight: '1.5rem', fontWeight: '400' },
      caption: { fontSize: '0.875rem', lineHeight: '1.25rem', fontWeight: '400' },
      small: { fontSize: '0.75rem', lineHeight: '1rem', fontWeight: '400' }
    },
    tablet: {
      h1: { fontSize: '2rem', lineHeight: '2.25rem', fontWeight: '700' },
      h2: { fontSize: '1.75rem', lineHeight: '2rem', fontWeight: '700' },
      h3: { fontSize: '1.5rem', lineHeight: '1.75rem', fontWeight: '600' },
      body: { fontSize: '1rem', lineHeight: '1.5rem', fontWeight: '400' },
      caption: { fontSize: '0.875rem', lineHeight: '1.25rem', fontWeight: '400' },
      small: { fontSize: '0.75rem', lineHeight: '1rem', fontWeight: '400' }
    }
  },
  
  // Mobile breakpoints
  breakpoints: {
    mobile: '320px',
    mobileLarge: '414px',
    tablet: '768px',
    tabletLarge: '1024px',
    desktop: '1280px'
  },
  
  // Mobile-specific animations
  animations: {
    swipe: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    tap: 'transform 0.1s ease-in-out',
    slide: 'transform 0.3s ease-in-out',
    fade: 'opacity 0.2s ease-in-out'
  },
  
  // Safe area insets
  safeArea: {
    top: 'env(safe-area-inset-top)',
    right: 'env(safe-area-inset-right)',
    bottom: 'env(safe-area-inset-bottom)',
    left: 'env(safe-area-inset-left)'
  }
};

// Export all tokens as default
export default {
  typography,
  spacing,
  cards,
  buttons,
  colors,
  radius,
  shadows,
  icons,
  motion,
  forms,
  layout,
  breakpoints,
  examples,
  mobileTokens,
};